import { State } from '@meraki-internal/state';
import { AILogsClient, AICallSource, IAICallDetail, IAICallSummary, IAILogsFilters } from './AILogsClient';
import { ILink } from '../caremap/WithLinks';
import { Logger } from '../support/Logger';

export interface IAILogsDetailView {
    title: string;
    calls: IAICallDetail[];
}

interface IAILogsStateData {
    calls: IAICallSummary[];
    nextLink?: ILink;
    filters: IAILogsFilters;
    isLoading: boolean;
    isLoadingMore: boolean;
    error?: string;
    detailView?: IAILogsDetailView;
    isDetailLoading: boolean;
}

export class AILogsState extends State<IAILogsStateData> {
    static inject = () => [AILogsClient, Logger];

    // Bumped on every fresh load so a slow response from a superseded filter combination
    // can't overwrite the list after a newer request already resolved.
    private loadToken = 0;

    constructor(private client: AILogsClient, private log: Logger) {
        super({
            calls: [],
            filters: { includeTestUsers: false },
            isLoading: false,
            isLoadingMore: false,
            isDetailLoading: false
        });
    }

    load = async (): Promise<void> => {
        const token = ++this.loadToken;
        this.setState({ isLoading: true, error: undefined, calls: [], nextLink: undefined });
        try {
            const response = await this.client.getRecent(this.state.filters);
            if (token !== this.loadToken) {
                return;
            }
            this.setState({ calls: response.calls, nextLink: response.links.next, isLoading: false });
        } catch (e) {
            this.failIfCurrent(token, e, 'isLoading', 'Failed to load AI call logs');
        }
    };

    loadMore = async (): Promise<void> => {
        const { nextLink } = this.state;
        if (!nextLink || this.state.isLoadingMore) {
            return;
        }
        const token = this.loadToken;
        this.setState({ isLoadingMore: true, error: undefined });
        try {
            const response = await this.client.getPage(nextLink);
            if (token !== this.loadToken) {
                return;
            }
            this.setState({
                calls: [...this.state.calls, ...response.calls],
                nextLink: response.links.next,
                isLoadingMore: false
            });
        } catch (e) {
            this.failIfCurrent(token, e, 'isLoadingMore', 'Failed to load more AI call logs');
        }
    };

    setSource = async (source?: AICallSource): Promise<void> => {
        this.setState({ filters: { ...this.state.filters, source } });
        await this.load();
    };

    setIncludeTestUsers = async (includeTestUsers: boolean): Promise<void> => {
        this.setState({ filters: { ...this.state.filters, includeTestUsers } });
        await this.load();
    };

    openConversation = async (call: IAICallSummary): Promise<void> => {
        const title = call.userEmail || call.userId;
        // Switch to the detail view immediately and let it show its own spinner; a spinner on
        // the list page pushed the rows down for a beat before the view swapped.
        this.setState({ detailView: { title, calls: [] }, isDetailLoading: true, error: undefined });
        try {
            const calls = call.links.conversation
                ? (await this.client.getConversation(call.links.conversation)).calls
                : [await this.client.getCall(call.links.detail)];
            this.setState({ isDetailLoading: false, detailView: { title, calls } });
        } catch (e) {
            this.failIfCurrent(this.loadToken, e, 'isDetailLoading', 'Failed to load call detail');
        }
    };

    closeDetail = (): void => {
        this.setState({ detailView: undefined, error: undefined });
    };

    private failIfCurrent = (token: number, e: unknown, loadingFlag: 'isLoading' | 'isLoadingMore' | 'isDetailLoading', fallbackMessage: string): void => {
        // Also surfaced in Sentry: this page fronts admin-only API endpoints, so a failure here
        // usually means a backend problem worth seeing without an admin reporting it.
        this.log.error(e);
        if (token !== this.loadToken) {
            return;
        }
        this.setState({
            [loadingFlag]: false,
            error: e instanceof Error ? e.message : fallbackMessage
        });
    };
}
